import { useId, type ReactNode } from 'react';
import { Box, Typography } from '@mui/material';
import type { SupportedLocale } from '@stm/contracts';
import { translate, type TranslationKey } from '@stm/i18n';

/** Native disclosure keeps forms mounted so closing a section never discards an edit. */
export function TaskSection({
  locale,
  titleKey,
  descriptionKey,
  children,
  defaultOpen = false,
}: {
  locale: SupportedLocale;
  titleKey: TranslationKey;
  descriptionKey?: TranslationKey;
  children: ReactNode;
  defaultOpen?: boolean;
}): React.JSX.Element {
  const titleId = useId();
  return (
    <Box
      component="details"
      open={defaultOpen}
      sx={{
        mb: 2,
        border: 1,
        borderColor: 'divider',
        borderRadius: 2,
        bgcolor: 'background.paper',
        '& > summary': {
          cursor: 'pointer',
          padding: { xs: 2, sm: 2.5 },
          minHeight: 60,
          borderRadius: 2,
        },
        '& > summary::marker': { color: '#174c43' },
        '&[open] > summary': {
          borderBottom: 1,
          borderColor: 'divider',
          borderRadius: '12px 12px 0 0',
        },
        '& > summary:hover': { bgcolor: '#f6f9f8' },
      }}
    >
      <Box component="summary" aria-labelledby={titleId}>
        <Typography component="span" id={titleId} sx={{ fontWeight: 650, ml: 1 }}>
          {translate(locale, titleKey)}
        </Typography>
        {descriptionKey !== undefined && (
          <Typography
            component="span"
            variant="body2"
            color="text.secondary"
            sx={{ display: 'block', mt: 0.5, ml: 2.5 }}
          >
            {translate(locale, descriptionKey)}
          </Typography>
        )}
      </Box>
      <Box sx={{ padding: { xs: 2, sm: 3 }, minWidth: 0 }}>{children}</Box>
    </Box>
  );
}
